import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
    Table2, Sigma, Plug, FileText, Pencil, Loader2, Check, X, Undo2, ListTree,
    TableProperties,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { PageLoader } from '../components/PageLoader';
import WorkingsModal from '../components/WorkingsModal';
import { InlineError } from '../components/InlineError';
import Tabs from '../components/Tabs';
import { columnLabel, formatFigure } from '../lib/model';
import type {
    AccountMapping, AccountsView, FigureDetail, FigureObservation, ModelCell,
    ModelGrid,
} from '../lib/types';

const ORIGIN = {
    derived: { label: 'From a connected source', icon: Plug },
    computed: { label: 'Worked out from your figures', icon: Sigma },
    extracted: { label: 'Interpreted from a document', icon: FileText },
    stated: { label: 'Stated', icon: Pencil },
} as const;

function CellPanel({ cell, unit, onChanged, onClose, onTrace }: {
    cell: ModelCell;
    unit: string;
    onChanged: () => void;
    onClose: () => void;
    onTrace: () => void;
}) {
    const { authFetch } = useAuth();
    const [detail, setDetail] = useState<FigureDetail | null>(null);
    const [amount, setAmount] = useState<string>(String(cell.value.amount ?? ''));
    const [note, setNote] = useState<string>(cell.note ?? '');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(() => {
        authFetch(`/knowledge/model/cell/${encodeURIComponent(cell.key)}`)
            .then(r => r.ok ? r.json() : Promise.reject(new Error('Could not load this figure')))
            .then((data: FigureDetail) => setDetail(data))
            .catch(err => setError(err.message));
    }, [authFetch, cell.key]);

    useEffect(load, [load]);

    const [org, concept, basis, grain, end] = cell.key.split(':').length > 5
        ? [
            cell.key.slice(0, cell.key.lastIndexOf(`:${cell.concept}:`)),
            cell.concept, cell.basis, cell.period.grain, cell.period.end,
        ]
        : cell.key.split(':');

    const save = async () => {
        const value = Number(amount);
        if (!Number.isFinite(value)) { setError('Enter a number'); return; }
        setBusy(true);
        setError(null);
        try {
            const res = await authFetch('/knowledge/model/cell', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    organization: org,
                    concept,
                    basis,
                    period: { start: cell.period.start, end, grain },
                    amount: value,
                    unit: cell.value.unit || undefined,
                    note: note || undefined,
                }),
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.detail || 'Could not save this figure');
            }
            setDetail(await res.json());
            onChanged();
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setBusy(false);
        }
    };

    const withdraw = async () => {
        setBusy(true);
        setError(null);
        try {
            const res = await authFetch(
                `/knowledge/model/cell/${encodeURIComponent(cell.key)}`, { method: 'DELETE' },
            );
            if (!res.ok) throw new Error('Could not withdraw this figure');
            setDetail(await res.json());
            onChanged();
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setBusy(false);
        }
    };

    const entered = (detail?.observations ?? []).some(o => o.kind === 'stated');

    return (
        <div className="border border-softgrey rounded-lg p-4 bg-white">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="text-sm font-medium">{columnLabel(cell.period)}</div>
                    <div className="text-xs text-coolslate">{cell.concept}</div>
                </div>
                <div className="flex items-center gap-1.5">
                    <button
                        type="button"
                        onClick={onTrace}
                        aria-label="What this figure was booked from"
                        title="What this figure was booked from"
                        className="text-coolslate hover:text-brand"
                    >
                        <TableProperties className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={onClose} aria-label="Close" className="text-coolslate hover:text-graphite">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>

            <ol className="mt-4 space-y-2">
                {(detail?.observations ?? []).map((observation, index) => (
                    <Observation
                        key={`${observation.kind}-${index}`}
                        observation={observation}
                        unit={unit}
                        inForce={index === 0}
                    />
                ))}
            </ol>

            <div className="mt-4 pt-4 border-t border-softgrey space-y-2">
                <label className="block text-xs font-medium text-coolslate" htmlFor="figure-amount">
                    Your figure
                </label>
                <input
                    id="figure-amount"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    inputMode="decimal"
                    className="w-full border border-softgrey rounded px-2 py-1.5 text-sm"
                />
                <input
                    aria-label="Why this figure is what it is"
                    placeholder="Why this figure is what it is (optional)"
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    className="w-full border border-softgrey rounded px-2 py-1.5 text-sm"
                />
                <p className="text-xs text-coolslate">
                    Your figure is what everything reads. What it overrides stays on the
                    record, and withdrawing yours puts it back.
                </p>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={save}
                        disabled={busy}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded bg-brand text-white hover:bg-brand-dark disabled:opacity-50"
                    >
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                        Save
                    </button>
                    {entered && (
                        <button
                            type="button"
                            onClick={withdraw}
                            disabled={busy}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded border border-softgrey hover:bg-offwhite disabled:opacity-50"
                        >
                            <Undo2 className="w-3.5 h-3.5" /> Withdraw mine
                        </button>
                    )}
                </div>
                {error && <InlineError message={error} />}
            </div>
        </div>
    );
}

function Observation({ observation, unit, inForce }: {
    observation: FigureObservation; unit: string; inForce: boolean;
}) {
    const origin = ORIGIN[observation.kind] ?? ORIGIN.stated;
    const Icon = origin.icon;
    const amount = observation.value?.amount;
    return (
        <li className={`text-sm rounded px-3 py-2 ${inForce ? 'bg-offwhite' : 'opacity-70'}`}>
            <div className="flex items-center justify-between gap-2">
                <span className="font-medium">
                    {formatFigure({ value: observation.value } as ModelCell, unit) ||
                        (amount ?? '')}
                </span>
                {inForce && <span className="text-xs text-coolslate">In force</span>}
            </div>
            <div className="flex items-center gap-1.5 text-xs text-coolslate mt-1">
                <Icon className="w-3 h-3 shrink-0" />
                {observation.source?.kind === 'source' && observation.source.id ? (
                    <>From <Link to={`/connections/${observation.source.id}`} className="text-brand hover:text-brand-dark">
                        {observation.source.name || 'a connected source'}
                    </Link></>
                ) : observation.source?.kind === 'document' && observation.source.id ? (
                    <>Interpreted from <Link to={`/files/${observation.source.id}`} className="text-brand hover:text-brand-dark">
                        {observation.source.name || 'a document'}
                    </Link></>
                ) : observation.source?.name && (observation.source.kind === 'person' || observation.source.kind === 'chat') ? (
                    `Stated by ${observation.source.name}`
                ) : origin.label}
            </div>
            {observation.derivation?.inputs?.length ? (
                <div className="text-xs text-coolslate mt-1">
                    Read {observation.derivation.inputs.length} figure
                    {observation.derivation.inputs.length === 1 ? '' : 's'} to work this out
                </div>
            ) : null}
            {observation.note && (
                <div className="text-xs text-graphite mt-1">{observation.note}</div>
            )}
        </li>
    );
}

function Sheet({ grid, onSelect, onTrace, selected }: {
    grid: ModelGrid;
    onSelect: (cell: ModelCell, unit: string) => void;
    onTrace: (cell: ModelCell, unit: string) => void;
    selected: string | null;
}) {
    if (!grid.rows.length) {
        return (
            <p className="text-sm text-coolslate">
                No figures yet. Connect an accounting system, or enter a figure once a
                company is on the record.
            </p>
        );
    }
    return (
        <div className="overflow-x-auto border border-softgrey rounded-lg bg-white">
            <table className="min-w-max w-full text-sm">
                <thead>
                    {/* The line names stay in view as the sheet scrolls across; the corner
                        sits above them, and both above the cells. */}
                    <tr>
                        <th className="text-left font-medium px-3 py-2 sticky left-0 top-0 z-30 bg-offwhite">Line</th>
                        {grid.columns.map(period => (
                            <th key={period.end} className="text-right font-medium px-3 py-2 whitespace-nowrap sticky top-0 z-20 bg-offwhite">
                                {columnLabel(period)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {grid.rows.map(row => (
                        <tr key={row.concept} className="border-t border-softgrey">
                            <th
                                scope="row"
                                className="text-left font-normal px-3 py-2 sticky left-0 z-10 bg-white whitespace-nowrap"
                                style={{ paddingLeft: `${0.75 + row.depth * 0.75}rem` }}
                            >
                                <span className={row.kind === 'metric' ? 'text-graphite' : ''}>{row.name}</span>
                                {row.kind === 'metric' && (
                                    <Sigma className="inline w-3 h-3 ml-1.5 text-coolslate" aria-label="Worked out" />
                                )}
                            </th>
                            {row.cells.map((cell, index) => (
                                <td key={grid.columns[index]?.end ?? index} className="px-0 py-0 text-right">
                                    {cell ? (
                                        <div className={`group flex items-center justify-end ${
                                            selected === cell.key ? 'bg-brand/10' : ''
                                        }`}>
                                            <button
                                                type="button"
                                                onClick={() => onTrace(cell, row.unit)}
                                                aria-label={`What ${row.name} for ${columnLabel(cell.period)} was booked from`}
                                                title="What this figure was booked from"
                                                className="opacity-0 group-hover:opacity-100 focus:opacity-100 pl-2 text-coolslate hover:text-brand"
                                            >
                                                <TableProperties className="w-3.5 h-3.5" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => onSelect(cell, row.unit)}
                                                className={`flex-1 text-right px-3 py-2 hover:bg-offwhite ${
                                                    cell.kind === 'stated' ? 'font-medium' : ''
                                                }`}
                                                title={ORIGIN[cell.kind]?.label}
                                            >
                                                {formatFigure(cell, row.unit)}
                                            </button>
                                        </div>
                                    ) : (
                                        <span className="block px-3 py-2 text-coolslate">–</span>
                                    )}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function Accounts() {
    const { authFetch } = useAuth();
    const [view, setView] = useState<AccountsView | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState<string | null>(null);

    const load = useCallback(() => {
        authFetch('/knowledge/accounts')
            .then(r => r.ok ? r.json() : Promise.reject(new Error('Could not load the chart of accounts')))
            .then((data: AccountsView) => setView(data))
            .catch(err => { setError(err.message); setView({ accounts: [], concepts: [] }); });
    }, [authFetch]);

    useEffect(load, [load]);

    const place = async (accountKey: string, concept: string | null) => {
        setBusy(accountKey);
        try {
            const res = await authFetch('/knowledge/accounts/map', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ account_key: accountKey, concept }),
            });
            if (!res.ok) throw new Error('Could not place this account');
            load();
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setBusy(null);
        }
    };

    const decide = async (mapId: string, verdict: 'accept' | 'reject') => {
        setBusy(mapId);
        try {
            const res = await authFetch(`/knowledge/accounts/suggestions/${mapId}/${verdict}`, {
                method: 'POST',
            });
            if (!res.ok) throw new Error('Could not record that');
            load();
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setBusy(null);
        }
    };

    if (view === null) return <PageLoader label="Loading the chart of accounts..." />;

    if (!view.accounts.length) {
        return (
            <p className="text-sm text-coolslate">
                No accounts yet. They arrive with an accounting system.
            </p>
        );
    }

    const placed = view.accounts.filter(a => a.statement === 'profit_and_loss');
    const balanceSheet = view.accounts.filter(a => a.statement === 'balance_sheet');
    const placedBy = (account: AccountMapping) =>
        account.decided_by === 'person' ? 'You' : account.decided_by === 'rule' ? 'Its type' : '—';

    return (
        <div>
            {error && <div className="mb-4"><InlineError message={error} /></div>}
            <p className="text-sm text-coolslate mb-4">
                Each income and expense account is placed on a line of the model by the type
                your accounting system gives it. Change a line here if you want more detail.
            </p>
            {placed.length > 0 && (
                <AccountTable heading="Income and expenses">
                    {placed.map(account => (
                        <tr key={account.key} className="border-t border-softgrey">
                            <AccountName account={account} />
                            <td className="px-3 py-2">
                                <select
                                    aria-label={`Line for ${account.name}`}
                                    value={account.concept ?? ''}
                                    disabled={busy === account.key}
                                    onChange={e => place(account.key, e.target.value || null)}
                                    className="border border-softgrey rounded px-2 py-1 text-sm"
                                >
                                    <option value="">On no line</option>
                                    {view.concepts.filter(c => c.kind === 'ledger').map(concept => (
                                        <option key={concept.id} value={concept.id}>{concept.name}</option>
                                    ))}
                                </select>
                                {account.suggestion && (
                                    <div className="flex items-center gap-2 mt-1.5">
                                        <span className="text-xs text-coolslate">
                                            {account.suggestion.concept_name}?
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => decide(account.suggestion!.id, 'accept')}
                                            disabled={busy === account.suggestion.id}
                                            className="text-xs px-2 py-0.5 rounded bg-brand text-white hover:bg-brand-dark"
                                        >
                                            Yes
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => decide(account.suggestion!.id, 'reject')}
                                            disabled={busy === account.suggestion.id}
                                            className="text-xs px-2 py-0.5 rounded border border-softgrey hover:bg-offwhite"
                                        >
                                            No
                                        </button>
                                    </div>
                                )}
                            </td>
                            <td className="px-3 py-2 text-coolslate">{placedBy(account)}</td>
                        </tr>
                    ))}
                </AccountTable>
            )}
            {balanceSheet.length > 0 && (
                <AccountTable
                    heading="Balance sheet"
                    note="These accounts need no line: cash, assets, liabilities and equity come from your accounting system's own balance sheet."
                >
                    {balanceSheet.map(account => (
                        <tr key={account.key} className="border-t border-softgrey">
                            <AccountName account={account} />
                            <td className="px-3 py-2 text-coolslate">
                                {account.concept_name ?? 'From the balance sheet'}
                            </td>
                            <td className="px-3 py-2 text-coolslate">{account.concept ? placedBy(account) : '—'}</td>
                        </tr>
                    ))}
                </AccountTable>
            )}
        </div>
    );
}

function AccountTable({ heading, note, children }: { heading: string; note?: string; children: React.ReactNode }) {
    return (
        <section className="mb-6">
            <h4 className="text-sm font-semibold text-graphite mb-1">{heading}</h4>
            {note && <p className="text-xs text-coolslate mb-2">{note}</p>}
            <div className="overflow-x-auto border border-softgrey rounded-lg">
                <table className="min-w-max w-full text-sm">
                    <thead>
                        <tr className="bg-offwhite">
                            <th className="text-left font-medium px-3 py-2">Account</th>
                            <th className="text-left font-medium px-3 py-2">Type</th>
                            <th className="text-left font-medium px-3 py-2">Line</th>
                            <th className="text-left font-medium px-3 py-2">Placed by</th>
                        </tr>
                    </thead>
                    <tbody>{children}</tbody>
                </table>
            </div>
        </section>
    );
}

function AccountName({ account }: { account: AccountMapping }) {
    return (
        <>
            <td className="px-3 py-2">
                {account.name}
                {account.code && <span className="text-coolslate ml-2">{account.code}</span>}
            </td>
            <td className="px-3 py-2 text-coolslate">{account.account_type || '—'}</td>
        </>
    );
}

export default function ModelPage() {
    const { authFetch } = useAuth();
    // ?tab=accounts opens the chart of accounts, so a link can point straight at it.
    const [params, setParams] = useSearchParams();
    const tab: 'sheet' | 'accounts' = params.get('tab') === 'accounts' ? 'accounts' : 'sheet';
    const setTab = (next: 'sheet' | 'accounts') =>
        setParams(next === 'accounts' ? { tab: 'accounts' } : {}, { replace: true });
    const [grid, setGrid] = useState<ModelGrid | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [company, setCompany] = useState<string>('');
    const [grain, setGrain] = useState<'month' | 'year'>('month');
    const [selected, setSelected] = useState<{ cell: ModelCell; unit: string } | null>(null);
    const [tracing, setTracing] = useState<{ cell: ModelCell; unit: string } | null>(null);

    const load = useCallback(() => {
        const params = new URLSearchParams({ grain });
        if (company) params.set('organization', company);
        authFetch(`/knowledge/model?${params.toString()}`)
            .then(r => r.ok ? r.json() : Promise.reject(new Error('Could not load the model')))
            .then((data: ModelGrid) => {
                setGrid(data);
                if (!company && data.companies.length === 1) setCompany(data.companies[0].key);
            })
            .catch(err => { setError(err.message); setGrid(null); });
    }, [authFetch, company, grain]);

    useEffect(load, [load]);

    const selectedCell = useMemo(() => {
        if (!selected || !grid) return null;
        for (const row of grid.rows) {
            for (const cell of row.cells) {
                if (cell && cell.key === selected.cell.key) return { cell, unit: row.unit };
            }
        }
        return selected;
    }, [selected, grid]);

    return (
        <div>
            <div className="pb-6 mb-6 border-b border-softgrey">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Table2 className="w-5 h-5 text-brand" /> Model
                </h3>
                <p className="text-sm text-coolslate mt-1">
                    Your figures by period, from the systems you have connected, worked out
                    where they can be, and yours where you say otherwise.
                </p>
            </div>

            <Tabs
                size="sm"
                items={[
                    { id: 'sheet' as const, label: 'Figures', icon: Table2 },
                    { id: 'accounts' as const, label: 'Accounts', icon: ListTree },
                ]}
                active={tab}
                onChange={setTab}
            />

            {tab === 'accounts' ? <Accounts /> : grid === null ? (
                error ? <InlineError message={error} /> : <PageLoader label="Loading the model..." />
            ) : (
                <div>
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                        {grid.companies.length > 1 && (
                            <select
                                aria-label="Company"
                                value={company}
                                onChange={e => { setCompany(e.target.value); setSelected(null); }}
                                className="border border-softgrey rounded px-2 py-1.5 text-sm"
                            >
                                <option value="">Every company</option>
                                {grid.companies.map(c => (
                                    <option key={c.key} value={c.key}>{c.name}</option>
                                ))}
                            </select>
                        )}
                        <select
                            aria-label="Period"
                            value={grain}
                            onChange={e => { setGrain(e.target.value as 'month' | 'year'); setSelected(null); }}
                            className="border border-softgrey rounded px-2 py-1.5 text-sm"
                        >
                            <option value="month">By month</option>
                            <option value="year">By year</option>
                        </select>
                    </div>

                    {error && <div className="mb-4"><InlineError message={error} /></div>}

                    <div className={`grid gap-6 ${selectedCell ? 'lg:grid-cols-[minmax(0,1fr)_20rem]' : ''}`}>
                        <Sheet
                            grid={grid}
                            selected={selectedCell?.cell.key ?? null}
                            onSelect={(cell, unit) => setSelected({ cell, unit })}
                            onTrace={(cell, unit) => setTracing({ cell, unit })}
                        />
                        {selectedCell && (
                            <CellPanel
                                cell={selectedCell.cell}
                                unit={selectedCell.unit}
                                onChanged={load}
                                onClose={() => setSelected(null)}
                                onTrace={() => setTracing(selectedCell)}
                            />
                        )}
                    </div>

                    {tracing && (
                        <WorkingsModal
                            cell={tracing.cell}
                            unit={tracing.unit}
                            organization={company || grid.organization || ''}
                            onClose={() => setTracing(null)}
                        />
                    )}
                </div>
            )}
        </div>
    );
}
